import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Network, Radar, Mail, CreditCard, ShieldCheck, Pencil, RotateCcw,
  Loader2, CheckCircle2, XCircle, Lock, Zap,
} from "lucide-react";

const SECTIONS = [
  { id: "bridge", title: "Directory Bridge", icon: Network, testTarget: "bridge", blurb: "Zoho contacts & clients sync from the main app." },
  { id: "loopcv", title: "Apply Series Engine", icon: Radar, testTarget: "loopcv", blurb: "Automated job discovery and matching." },
  { id: "email", title: "Email Delivery", icon: Mail, testTarget: "resend", blurb: "OTP codes and application emails via Resend." },
  { id: "payments", title: "Payments", icon: CreditCard, testTarget: "razorpay", blurb: "Pro subscription checkout via Razorpay." },
  { id: "access", title: "Access Control", icon: ShieldCheck, testTarget: null, blurb: "Direct login gate and admin emails." },
];

const SOURCE_STYLES = {
  db: "bg-amber-100 text-amber-700 border-amber-200",
  env: "bg-slate-100 text-slate-600 border-slate-200",
  unset: "bg-rose-50 text-rose-600 border-rose-200",
};
const SOURCE_LABELS = { db: "Override", env: "From env", unset: "Not set" };

function SettingRow({ item, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (value) => {
    setBusy(true);
    try {
      await api.put(`/admin/settings/${item.key}`, { value });
      toast.success(`${item.label} saved.`);
      setEditing(false);
      setDraft("");
      onSaved();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not save setting.");
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/settings/${item.key}`);
      toast.success(`${item.label} reverted to environment value.`);
      onSaved();
    } catch {
      toast.error("Could not revert setting.");
    } finally {
      setBusy(false);
    }
  };

  if (item.kind === "bool") {
    const on = (item.value || "").toLowerCase() === "true";
    return (
      <div data-testid={`settings-row-${item.key}`} className="flex items-center justify-between gap-4 py-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{item.label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
          <span className={`inline-block mt-1.5 px-2 py-0.5 rounded-md border text-[10px] font-semibold ${SOURCE_STYLES[item.source]}`}>
            {SOURCE_LABELS[item.source]}
          </span>
        </div>
        <Switch
          data-testid={`settings-toggle-${item.key}`}
          checked={on}
          disabled={busy}
          onCheckedChange={(checked) => save(checked ? "true" : "false")}
        />
      </div>
    );
  }

  return (
    <div data-testid={`settings-row-${item.key}`} className="py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{item.label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-md border text-[10px] font-semibold ${SOURCE_STYLES[item.source]}`}>
          {SOURCE_LABELS[item.source]}
        </span>
      </div>

      {editing ? (
        <div className="mt-3 space-y-2">
          {item.kind === "csv" ? (
            <Textarea
              data-testid={`settings-input-${item.key}`}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="email1@example.com, email2@example.com"
              className="bg-white border-slate-300 text-slate-900 text-sm min-h-[70px]"
            />
          ) : (
            <Input
              data-testid={`settings-input-${item.key}`}
              type={item.secret ? "password" : "text"}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={item.secret ? "Enter new value" : "Enter value"}
              className="bg-white border-slate-300 text-slate-900 h-10"
            />
          )}
          <div className="flex gap-2">
            <Button data-testid={`settings-save-${item.key}`} onClick={() => save(draft)} disabled={busy || !draft.trim()}
              className="h-9 bg-amber-500 text-black hover:bg-amber-400 font-semibold text-sm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
            </Button>
            <Button variant="outline" onClick={() => { setEditing(false); setDraft(""); }} disabled={busy}
              className="h-9 border-slate-300 text-slate-600 text-sm">
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2">
          <code className="flex-1 truncate rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700 font-mono">
            {item.value || "Not set"}
          </code>
          <button data-testid={`settings-replace-${item.key}`} onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors">
            <Pencil className="w-3.5 h-3.5" /> {item.source === "unset" ? "Set" : "Replace"}
          </button>
          {item.source === "db" && (
            <button data-testid={`settings-revert-${item.key}`} onClick={revert} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors">
              <RotateCcw className="w-3.5 h-3.5" /> Revert
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function AdminSettings() {
  const [access, setAccess] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  const load = useCallback(async () => {
    try {
      const { data: accessData } = await api.get("/admin/access");
      setAccess(accessData.is_admin);
      if (accessData.is_admin) {
        const { data } = await api.get("/admin/settings");
        setItems(data.items);
      }
    } catch {
      setAccess(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runTest = async (target) => {
    setTesting((t) => ({ ...t, [target]: true }));
    setTestResults((r) => ({ ...r, [target]: null }));
    try {
      const { data } = await api.post(`/admin/test/${target}`);
      setTestResults((r) => ({ ...r, [target]: data }));
    } catch {
      setTestResults((r) => ({ ...r, [target]: { ok: false, message: "Test request failed" } }));
    } finally {
      setTesting((t) => ({ ...t, [target]: false }));
    }
  };

  if (loading) {
    return (
      <div className="py-24 text-center" data-testid="admin-settings-loading">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-amber-500" />
      </div>
    );
  }

  if (!access) {
    return (
      <div className="py-24 text-center" data-testid="admin-settings-denied">
        <Lock className="w-10 h-10 mx-auto mb-3 text-slate-300" />
        <p className="font-semibold text-slate-800">Admins only</p>
        <p className="text-sm text-slate-500 mt-1">Your account is not on the admin allowlist.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-settings-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">Settings</h1>
        <p className="text-slate-500 mt-1">Configure integrations and access. Saved overrides apply instantly — no restarts, no .env edits.</p>
      </div>

      {SECTIONS.map((section) => {
        const rows = items.filter((i) => i.section === section.id);
        if (!rows.length) return null;
        const result = section.testTarget ? testResults[section.testTarget] : null;
        return (
          <section key={section.id} data-testid={`settings-section-${section.id}`}
            className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4 pb-2 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center">
                  <section.icon className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-900">{section.title}</h2>
                  <p className="text-xs text-slate-500">{section.blurb}</p>
                </div>
              </div>
              {section.testTarget && (
                <div className="flex items-center gap-2 shrink-0">
                  {result && (
                    <span data-testid={`test-result-${section.testTarget}`}
                      className={`inline-flex items-center gap-1 text-xs font-medium ${result.ok ? "text-emerald-600" : "text-rose-600"}`}>
                      {result.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                      {result.message}
                    </span>
                  )}
                  <Button data-testid={`test-button-${section.testTarget}`} variant="outline"
                    onClick={() => runTest(section.testTarget)} disabled={testing[section.testTarget]}
                    className="border-amber-300 text-amber-700 hover:bg-amber-50 text-sm font-semibold h-9">
                    {testing[section.testTarget]
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <><Zap className="w-3.5 h-3.5 mr-1.5" /> Test Connection</>}
                  </Button>
                </div>
              )}
            </div>
            <div className="divide-y divide-slate-100">
              {rows.map((item) => (
                <SettingRow key={item.key} item={item} onSaved={load} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
